/**
 * Audit Log Modal Component
 * Admin-only interface for viewing system audit logs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import './Modal.css';

const AuditModal = ({ isOpen, onClose, language }) => {
  const { authFetch, user: currentUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    user_id: '',
    action: ''
  });

  // Translations
  const t = {
    title: language === 'he' ? 'יומן פעולות' : 'Audit Log',
    timestamp: language === 'he' ? 'תאריך ושעה' : 'Timestamp',
    user: language === 'he' ? 'משתמש' : 'User',
    action: language === 'he' ? 'פעולה' : 'Action',
    details: language === 'he' ? 'פרטים' : 'Details',
    ipAddress: language === 'he' ? 'כתובת IP' : 'IP Address',
    filterByUser: language === 'he' ? 'סנן לפי משתמש' : 'Filter by User',
    filterByAction: language === 'he' ? 'סנן לפי פעולה' : 'Filter by Action',
    allUsers: language === 'he' ? 'כל המשתמשים' : 'All Users',
    allActions: language === 'he' ? 'כל הפעולות' : 'All Actions',
    noLogs: language === 'he' ? 'אין פעולות להצגה' : 'No audit logs found',
    loadError: language === 'he' ? 'שגיאה בטעינת יומן' : 'Error loading audit logs',
    previous: language === 'he' ? 'הקודם' : 'Previous',
    next: language === 'he' ? 'הבא' : 'Next',
    pageOf: language === 'he' ? 'עמוד {page} מתוך {total}' : 'Page {page} of {total}',
    refresh: language === 'he' ? 'רענן' : 'Refresh',
    export: language === 'he' ? 'ייצא' : 'Export'
  };

  // Action translations
  const actionLabels = {
    LOGIN: language === 'he' ? 'התחברות' : 'Login',
    LOGOUT: language === 'he' ? 'התנתקות' : 'Logout',
    LOGIN_FAILED: language === 'he' ? 'התחברות נכשלה' : 'Login Failed',
    PASSWORD_CHANGED: language === 'he' ? 'סיסמה שונתה' : 'Password Changed',
    PROFILE_UPDATED: language === 'he' ? 'פרופיל עודכן' : 'Profile Updated',
    USER_CREATED: language === 'he' ? 'משתמש נוצר' : 'User Created',
    USER_UPDATED: language === 'he' ? 'משתמש עודכן' : 'User Updated',
    USER_DELETED: language === 'he' ? 'משתמש הושבת' : 'User Deactivated',
    USER_ACTIVATED: language === 'he' ? 'משתמש הופעל' : 'User Activated',
    ADMIN_PASSWORD_RESET: language === 'he' ? 'איפוס סיסמה' : 'Password Reset'
  };

  const actionTypes = [
    'LOGIN', 'LOGOUT', 'LOGIN_FAILED', 'PASSWORD_CHANGED',
    'PROFILE_UPDATED', 'USER_CREATED', 'USER_UPDATED',
    'USER_DELETED', 'USER_ACTIVATED', 'ADMIN_PASSWORD_RESET'
  ];

  // Fetch audit logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    const params = new URLSearchParams({
      page: page.toString(),
      per_page: '20'
    });

    if (filters.user_id) {
      params.append('user_id', filters.user_id);
    }
    if (filters.action) {
      params.append('action', filters.action);
    }

    try {
      const response = await authFetch(`/api/auth/audit-logs?${params}`);
      const data = await response.json();

      if (response.ok) {
        setLogs(data.logs || []);
        setTotalPages(data.pages || 1);
      } else {
        setError(data.error || t.loadError);
      }
    } catch (err) {
      setError(t.loadError);
    } finally {
      setLoading(false);
    }
  }, [authFetch, page, filters, t.loadError]);

  // Fetch users for filter dropdown
  const fetchUsers = useCallback(async () => {
    try {
      const response = await authFetch('/api/auth/users?include_inactive=true');
      const data = await response.json();
      if (response.ok) {
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
      fetchUsers();
    }
  }, [isOpen, fetchLogs, fetchUsers]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const handleExport = () => {
    // Export current logs to CSV
    const headers = ['Timestamp', 'User', 'Action', 'Entity', 'IP Address'];
    const rows = logs.map(log => [
      new Date(log.timestamp).toISOString(),
      log.user_email || log.user_id,
      log.action,
      log.entity_type ? `${log.entity_type}:${log.entity_id}` : '',
      log.ip_address || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString(language === 'he' ? 'he-IL' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  // Check if current user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{t.title}</h2>
            <button className="close-btn" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
          </div>
          <div className="modal-body">
            <p className="text-center">
              {language === 'he' ? 'אין לך הרשאה לצפות בעמוד זה' : 'You do not have permission to view this page'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t.title}</h2>
          <button className="close-btn" onClick={onClose} title={language === 'he' ? 'סגור' : 'Close'}>×</button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          {/* Filters */}
          <div className="audit-filters">
            <div className="filter-group">
              <label>{t.filterByUser}</label>
              <select
                name="user_id"
                value={filters.user_id}
                onChange={handleFilterChange}
              >
                <option value="">{t.allUsers}</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || `${user.first_name} ${user.last_name}`} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>{t.filterByAction}</label>
              <select
                name="action"
                value={filters.action}
                onChange={handleFilterChange}
              >
                <option value="">{t.allActions}</option>
                {actionTypes.map(action => (
                  <option key={action} value={action}>
                    {actionLabels[action] || action}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={fetchLogs}>
                🔄 {t.refresh}
              </button>
              <button className="btn btn-secondary" onClick={handleExport}>
                📥 {t.export}
              </button>
            </div>
          </div>

          {/* Audit Log Table */}
          <div className="audit-table-container">
            {loading ? (
              <div className="loading-spinner">Loading...</div>
            ) : logs.length === 0 ? (
              <p className="text-center">{t.noLogs}</p>
            ) : (
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>{t.timestamp}</th>
                    <th>{t.user}</th>
                    <th>{t.action}</th>
                    <th>{t.details}</th>
                    <th>{t.ipAddress}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td className="timestamp-cell">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td>{log.user_email || `User #${log.user_id}`}</td>
                      <td>
                        <span className={`action-badge action-${log.action?.toLowerCase()}`}>
                          {actionLabels[log.action] || log.action}
                        </span>
                      </td>
                      <td>
                        {log.entity_type && (
                          <span className="entity-info">
                            {log.entity_type}: {log.entity_id}
                          </span>
                        )}
                      </td>
                      <td className="ip-cell">{log.ip_address || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                {t.previous}
              </button>
              <span className="page-info">
                {t.pageOf.replace('{page}', page).replace('{total}', totalPages)}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                {t.next}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditModal;
